import React, { useEffect, useState } from 'react';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';

const labels = { confirmed: 'Подтверждено', failed: 'Обмен не исполнен', pending: 'Ожидает подтверждения', needs_review: 'Требует сверки' };
const short = address => address ? `${address.slice(0, 6)}…${address.slice(-4)}` : 'Адрес не определён';
const when = timestamp => timestamp ? new Date(timestamp).toLocaleString('ru-RU', { timeZoneName: 'short' }) : '—';

export default function VerifiedOperationHistory({ tokenSymbol, emergency }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const limit = 100;
  const [offset, setOffset] = useState(0);
  const [tab, setTab] = useState('market');
  useEffect(() => {
    setResult(null); setError('');
    let active = true, busy = false;
    const controller = new AbortController();
    const poll = async () => {
      if (busy) return;
      busy = true;
      try {
        const response = await getAuthenticatedAxios().get(`${getApiUrl()}/api/market-maker/history/verified?limit=${limit}&offset=${offset}`, {
          signal: controller.signal, timeout: 15000,
        });
        if (active) { setResult(response.data); setError(''); }
      } catch (e) {
        if (active) setError('Не удалось обновить историю. Показана последняя загруженная версия.');
      } finally { busy = false; }
    };
    poll(); const timer = setInterval(poll, 15000);
    return () => { active = false; controller.abort(); clearInterval(timer); };
  }, [offset, tokenSymbol]);
  const rows = (result?.history || []).filter(row => tab === 'all' ||
    (tab === 'errors' ? row.state !== 'confirmed' : row.state === 'confirmed' && (tab === 'ours' ? row.is_our_wallet : !row.is_our_wallet)));
  const sync = result?.sync || {};
  const quotes = sync.current_ton_quotes;
  const stale = !sync.last_success || Date.now() - new Date(sync.last_success).getTime() > 120000;
  return <Card>
    <CardHeader>
      <CardTitle>История обменов {tokenSymbol}</CardTitle>
      <CardDescription>Подтверждённые переводы в блокчейне. Дата и время — в часовом поясе браузера.</CardDescription>
    </CardHeader>
    <CardContent>
      {emergency?.stopped && <p role="status" className="mb-3 text-amber-700">Исполнение остановлено: {emergency.reason || 'аварийная остановка'}. Наблюдение за рынком продолжается.</p>}
      <p className="text-xs text-gray-500 mb-2">Сверка: {when(sync.last_success)} · {result?.total ?? 0} записей
        {!sync.backfill_complete && ' · Загружается архив'}
      </p>
      {sync.legacy_unverified > 0 && <p className="text-xs text-amber-700 mb-2">{sync.legacy_unverified} старых записей не подтверждены и сохранены в разделе «Возвраты и проверка» без сумм.</p>}
      {(error || sync.error || (result && stale)) && <p role="alert" className="text-amber-700 mb-3">{error || 'Сверка с блокчейном задерживается. Последние события могут отсутствовать.'}</p>}
      <div className="rounded border bg-slate-50 p-3 mb-3 text-sm">
        <p>Исторический курс TON/USD не подтверждён. Суммы показаны в TON и {tokenSymbol}; комиссии сети учитываются отдельно.</p>
        {quotes?.sources?.length > 0 && <p className="mt-1 text-xs text-gray-600">Котировки сейчас (получены {when(quotes.fetched_at)}): {quotes.sources.map(q => `${q.source}: ${q.price} ${q.currency}/TON`).join(' · ')}. К старым сделкам не применяются.</p>}
        {quotes?.disagreement && <p role="alert" className="text-amber-700 mt-1">Источники курса расходятся. Единая достоверная USD-оценка недоступна.</p>}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {[['market', 'Рынок'], ['ours', 'Наши кошельки'], ['errors', 'Возвраты и проверка'], ['all', 'Все']].map(([id, name]) =>
          <Button key={id} variant={tab === id ? 'default' : 'outline'} onClick={() => setTab(id)}>{name}</Button>)}
      </div>
      {!result && !error && <p>Загрузка истории…</p>}
      {result && !rows.length && <p className="text-gray-500 py-4">{sync.backfill_complete ? 'В загруженной части истории нет операций этого типа.' : 'Идёт сверка с блокчейном. Подтверждённые данные появятся автоматически.'}</p>}
      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {rows.map(row => <div key={row.id} className="rounded border p-3 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <strong>{row.source === 'legacy' ? 'Непроверенная архивная запись' : row.is_our_wallet ? 'Наш кошелёк' : 'Внешняя операция'} · {row.side === 'buy' ? 'Покупка' : row.side === 'sell' ? 'Продажа' : 'Обмен'}</strong>
            <span className={row.state === 'confirmed' ? 'text-green-700' : 'text-amber-700'}>{labels[row.state] || row.state}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{when(row.timestamp)} · {row.dex}</p>
          {row.state === 'confirmed' ? <p className="font-mono mt-2 break-words">
            {row.side === 'buy' ? `${row.ton_amount} TON → ${row.token_amount} ${tokenSymbol}` : `${row.token_amount} ${tokenSymbol} → ${row.ton_amount} TON`}
          </p> : <p className="mt-2">{row.refund_ton_amount ? `Возврат ${row.refund_ton_amount} TON. Обмен не засчитан.` : 'Объём не включён в исполненные обмены.'}</p>}
          {row.price_ton && <p className="text-xs text-gray-500 mt-1" title={row.price_ton}>Цена исполнения: {Number(row.price_ton).toPrecision(10)} TON/{tokenSymbol}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
            {row.wallet && <a className="text-blue-600" href={`https://tonviewer.com/${row.wallet}`} target="_blank" rel="noopener noreferrer">{row.side === 'buy' ? 'Получатель' : 'Отправитель'}: {short(row.wallet)}</a>}
            <a className="text-blue-600" href={`https://tonviewer.com/transaction/${row.trace_id}`} target="_blank" rel="noopener noreferrer">Событие {row.trace_id.slice(0, 8)}…</a>
          </div>
          {row.sender !== row.recipient && <p className="text-xs text-gray-500 mt-1">Отправитель в пул: {short(row.sender)} · Получатель: {short(row.recipient)}</p>}
        </div>)}
      </div>
      <div className="flex flex-wrap gap-3 items-center mt-3">
        <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - limit))}>Новее</Button>
        <span className="text-xs text-gray-500">Страница {Math.floor(offset / limit) + 1} · фильтр применяется к этой странице</span>
        <Button variant="outline" disabled={!result?.has_more} onClick={() => setOffset(value => value + limit)}>Старее</Button>
      </div>
    </CardContent>
  </Card>;
}
